import React from 'react'
import { Grid, Typography } from '@mui/material'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import MatchState from 'smartstream-widgets/controls/MatchState/MatchState'
import SimpleScores from 'smartstream-widgets/controls/SimpleScores/SimpleScores'
import StatCounter from 'smartstream-widgets/controls/StatCounter/StatCounter'
import TeamLogo from 'smartstream-widgets/controls/TeamLogo/TeamLogo'
import { HighestScore, Props } from './types'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { CompetitorWithFixtureData, Records } from 'smartstream-widgets/types/competitor'
import { findCompetitorBySide } from 'smartstream-widgets/utils/competitors'
import { MatchState as MatchStateType } from 'smartstream-widgets/types/sport'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) =>  createStyles({
  competitors: {
    gridColumn: 2,
    gridRow: 2,
    minWidth: 0,
    width: 'auto'
  },
  dateCaption: {
    fontSize: 9
  },
  liveCaption: {
    display: 'unset',
    color: theme.palette.text.primary,
    marginLeft: '1ch'
  },
  dateContainer: {
    gridColumn: 2
  },
  draws: {
    marginLeft: 26
  },
  highlight: {
    color: theme.palette.text.primary
  },
  logo: {
    boxSizing: 'border-box',
    flexShrink: 0,
    width: 20,
    height: 20,
    marginRight: 6,
    objectFit: 'contain'
  },
  noHighlight: {
    color: theme.palette.text.secondary
  },
  records: {
    fontSize: 12
  },
  rightTriangle: {
    '&::after': {
      content: '""',
      border: '8px solid transparent',
      borderRight: `8px solid ${theme.palette.grey['800']}`,
      position: 'absolute',
      right: 0
    }
  },
  root: {
    boxSizing: 'border-box',
    display: 'grid',
    flexShrink: 0,
    gridTemplateColumns: 'minmax(10px,auto) 1fr',
    gridTemplateRows: 'auto 1fr',
    padding: `5px 0px 5px 5px`,
    position: 'relative'
  },
  score: {
    fontSize: 16,
    fontWeight: 700
  },
  scoreContainer: {
    margin: `0px 14px 0px 0px`
  },
  selectionName: {
    color: theme.palette.text.primary,
    flex: '1 75px',
    fontSize: 14,
    paddingRight: 10
  },
  selection: {
    flexGrow: 1,
    minHeight: 30,
    minWidth: 0,
    position: 'relative',
    '& svg$logo': {
      filter: `drop-shadow(0 2px 1px ${theme.palette.grey['500']})`
    }
  },
  position: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'center',
    fontSize: 14,
    gridColumn: 1,
    gridRow: '1/-1',
    marginRight: 4,
    width: 28
  }
})

export function getHighestScore(competitors: Array<CompetitorWithFixtureData>): HighestScore {
  const initialAcc: { currentScore: number, repeated: boolean } = { currentScore: 0, repeated: false }
  return competitors.reduce((acc, comp) => {
    if (comp.currentScore === acc.currentScore) acc.repeated = true
    if ((comp.currentScore || 0) > acc.currentScore) {
      acc.currentScore = comp.currentScore || 0
      acc.repeated = false
    }
    return acc
  }, initialAcc)
}

export function VerticalScoreBoard (props: Props) {
  const {
    animationDuration,
    classes,
    config: {
      showSeasonRecords = false
    },
    competitionId,
    competitors = [],
    hasDraws,
    isAwayTeamFirst = false,
    matchState = {} as MatchStateType,
    position,
    scheduledStartTime,
    translations
  } = props
  const inPlay = matchState && matchState.matchStatus === 'InPlay'
  const highestScore = inPlay && getHighestScore(competitors)
  let orderedCompetitor = competitors
  if (competitors.length === 2) {
    const awayCompetitor = findCompetitorBySide(competitors, 'AWAY')
    const homeCompetitor = findCompetitorBySide(competitors, 'HOME')
    if (awayCompetitor && homeCompetitor) {
      orderedCompetitor = isAwayTeamFirst ? [awayCompetitor, homeCompetitor] : [homeCompetitor, awayCompetitor]
    }
  }

  return (
    <Grid container direction='column' className={classes.root}>
      {
        position &&
        <Typography className={classes.position} data-testid='position'>{position}</Typography>
      }
      <MatchState
        colorInverted
        classes={{root: classes.dateContainer ,dateCaption: classes.dateCaption, liveCaption: classes.liveCaption}}
        matchPhase={matchState && matchState.phase}
        matchStatus={matchState && matchState.matchStatus}
        scheduledStartTime={scheduledStartTime}
      />
      <Grid container className={classes.competitors} direction='column'>
        {
          orderedCompetitor.map(comp => {
            const { competitorId, officialName, shortName, displayName, currentScore, records = {} as Records } = comp
            const hasHighestScore = highestScore && highestScore.currentScore === currentScore
            const selectionArrowClass = hasHighestScore && highestScore.repeated === false ?
              classes.rightTriangle : ''
            const scoreHighlightClass = hasHighestScore ? classes.highlight : classes.noHighlight
            const useDisplayName = displayName && displayName[0]
            const defaultName = shortName || officialName
            const competitorDisplayName = useDisplayName? displayName.join(' ') : defaultName

            return (
              <Grid
                data-testid='vertical-scoreboard-competitor'
                alignItems='center'
                className={`${classes.selection} ${selectionArrowClass}`}
                container
                key={competitorId}
                wrap='nowrap'
              >
                <TeamLogo
                  competitor={comp}
                  className={classes.logo}
                  competitionId={competitionId} />
                <Typography className={classes.selectionName} noWrap>{competitorDisplayName}</Typography>
                {
                  inPlay
                  ? <StatCounter
                    animationDuration={animationDuration}
                    animationType='slideUp'
                    className={`${classes.scoreContainer} ${classes.score} ${scoreHighlightClass}`}
                    stat={currentScore}
                  />
                  : showSeasonRecords &&
                  <SimpleScores
                    animationType={'slideUp'}
                    animationDuration={animationDuration}
                    classes={{root: `${classes.scoreContainer} ${classes.records}`}}
                    highlight='both'
                    leftScore={records.wins}
                    middleScore={hasDraws && records.draws}
                    rightScore={records.losses}
                    />
                }
              </Grid>
            )
          })
        }
        {
          hasDraws &&
          <Grid container alignItems='center' className={classes.selection} data-testid='vertical-scoreboard-draws'>
            <Typography className={`${classes.selectionName} ${classes.draws}`}>
              {translations.drawsLabel}
            </Typography>
          </Grid>
        }
      </Grid>
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'VerticalScoreBoard', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      drawsLabel: t('verticalscoreboard.draws.translation', {
        defaultValue: 'Draw',
        context: 'Text for draws selection in VerticalScoreBoard component'
      })
    }
  })
)(VerticalScoreBoard)